import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useSEO } from '../../../hooks/useSEO';
import type { CSVIndexEntry } from '../../../lib/filamentStressStrain';

// 方向キーをパターン番号に変換（pattern1→1, pattern2→2, pattern3→3）
const orientationToPattern = (pattern: string): number => {
  const map: Record<string, number> = {
    'pattern1': 1,
    'pattern2': 2,
    'pattern3': 3
  };
  return map[pattern.toLowerCase()] || 0;
};

// 材料名からフィラメント個別ページのパスを取得（実在ルートのみ）
const getFilamentPagePath = (materialName: string): string | null => {
  // 材料名を正規化（大文字化、トリム）
  const normalized = materialName.trim().toUpperCase();
  
  // 材料名 → パスのマッピング（実在するルートのみ）
  const materialPathMap: Record<string, string> = {
    'PLA': '/filament/pla',
    'PLA+': '/filament/pla-plus',
    'PLA-PLUS': '/filament/pla-plus',
    'PLA-CF': '/filament/pla-cf',
    'PLA-SILK': '/filament/pla-silk',
    'MATTE-PLA': '/filament/matte-pla',
    'PLA-PRO': '/filament/pla-pro',
    'HT-PLA': '/filament/ht-pla',
    'PETG': '/filament/petg',
    'PETG-CF': '/filament/petg-cf',
    'PETG-GF': '/filament/petg-gf',
    'ABS': '/filament/abs',
    'ABS-CF': '/filament/abs-cf',
    'ABS-GF': '/filament/abs-gf',
    'ASA': '/filament/asa',
    'ASA-CF': '/filament/asa-cf',
    'ASA-GF': '/filament/asa-gf',
    'PC': '/filament/pc',
    'PC-CF': '/filament/pc-cf',
    'PC-GF': '/filament/pc-gf',
    'PA': '/filament/pa',
    'PA（ナイロン）': '/filament/pa',
    'PA-CF': '/filament/pa-cf',
    'PA-GF': '/filament/pa-gf',
    'TPU': '/filament/tpu'
  };
  
  return materialPathMap[normalized] || null;
};

// 材料名の表示ラベルを取得（内部値と表示を分離）
const formatMaterialLabel = (materialName: string): string => {
  const normalized = materialName.trim().toUpperCase();
  if (normalized === 'PLA-PLUS') {
    return 'PLA+';
  }
  return materialName;
};

type SortKey = 'type' | 'maker' | 'color';
type SortOrder = 'asc' | 'desc';

// CSVIndexEntry型を拡張して buyLinks を含める
interface ExtendedCSVIndexEntry extends CSVIndexEntry {
  buyLinks?: {
    amazon?: string;
    official?: string;
  };
}

// グルーピング後の行の型
interface GroupedFilamentRow {
  maker: string;
  type: string;
  color: string;
  buyLinks?: {
    amazon?: string;
    official?: string;
  };
  patternLinks: Record<number, string>; // パターン番号 -> CSV URL
}

const DataDownloadPage: React.FC = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en/');

  const TEXT_JA = {
    title: 'フィラメントデータ ダウンロード',
    seoTitle: 'フィラメントデータ ダウンロード | 3Dプリンター フィラメント試験データ',
    seoDescription: '3Dプリンター用フィラメントの曲げ試験データ（CSV形式）をダウンロードできます。応力-ひずみ曲線データを個人利用・研究・学習にご活用ください。',
    seoKeywords: 'フィラメントデータ,ダウンロード,CSV,曲げ試験,応力ひずみ,3Dプリンター,PLA,PETG,ABS',
    description: '3Dプリンター用フィラメントの曲げ試験データ（CSV形式）をダウンロードできます。',
    description2: '応力-ひずみ曲線データを個人利用・研究・学習にご活用ください。',
    dataUsageTitle: 'データ利用について（重要）',
    dataUsagePersonal: '個人利用は自由です。',
    dataUsagePersonalDetail: '研究・学習・個人プロジェクトにご自由にお使いください。',
    dataUsagePublic: '動画・記事・SNS等での公開、データの転載・再配布を行う場合は必ず事前にお問い合わせください。',
    dataUsageDetail: '詳細は',
    dataUsageLink: 'データ利用規約',
    dataUsageConfirm: 'をご確認ください。',
    csvListTitle: '曲げ試験データ（CSV）',
    csvListDescription: '応力-ひずみ曲線データをCSV形式でダウンロードできます。各ファイルには試験条件（スパン、試験片寸法）とデータポイント（ひずみ%、応力MPa）が含まれています。',
    loading: 'データ読み込み中...',
    error: 'データの読み込みに失敗しました',
    noData: '現在ダウンロード可能なデータはありません。',
    filterMaterial: '材料（複数選択可）',
    filterManufacturer: 'メーカー（複数選択可）',
    filterPattern: 'パターン（複数選択可）',
    filterKeyword: 'キーワード',
    filterKeywordPlaceholder: '検索...',
    filterClear: 'クリア',
    filterDisplay: '表示:',
    filterTotal: '件',
    filterAllTotal: '（全',
    filterAllTotalEnd: '件）',
    tableHeaderMaterial: '材料',
    tableHeaderManufacturer: 'メーカー',
    tableHeaderColor: '色',
    tableHeaderPurchase: '購入リンク',
    tableHeaderCSV: 'CSV（パターン）',
    purchaseAmazon: 'Amazon',
    purchaseOfficial: '公式',
    pattern1: 'パターン1',
    pattern2: 'パターン2',
    pattern3: 'パターン3',
    dataFormatTitle: 'データ形式について',
    dataFormatCSV: 'CSV形式：',
    dataFormatHeader: 'ヘッダー行（# で始まる）：試験条件（スパン、試験片寸法）',
    dataFormatColumns: 'データ列：',
    dataFormatStrain: '（ひずみ %）、',
    dataFormatStress: '（応力 MPa）',
    dataFormatEncoding: '文字コード：UTF-8',
    dataFormatDelimiter: '区切り文字：カンマ（,）',
    patternAbout: 'パターンについて：',
    patternDesc1: '：水平方向（最も一般的）',
    patternDesc2: '：Z軸方向',
    patternDesc3: '：X軸方向',
    testMethodLink: '曲げ強度試験方法（測定条件）はこちら',
    noticeTitle: 'ご注意',
    notice1: 'データは3点曲げ試験の結果から計算されています。',
    notice2: '試験条件（スパン、試験片寸法、速度など）により結果は変動します。',
    notice3: 'データは参考値であり、実際の使用環境での性能を保証するものではありません。',
    notice4: 'データの正確性については最善を尽くしていますが、誤りがある可能性があります。',
    notice5: 'データは予告なく更新・追加される場合があります。',
    backToList: 'フィラメント一覧に戻る',
    breadcrumbHome: 'ホーム',
    breadcrumbFilament: 'フィラメントデータ',
    breadcrumbDownload: 'データダウンロード',
    mobileMaterial: '材料:',
    mobileManufacturer: 'メーカー:',
    mobileColor: '色:',
    mobilePurchase: '購入:',
    mobileCSV: 'CSV（パターン）:'
  };

  const TEXT_EN = {
    title: 'Filament Data Download',
    seoTitle: 'Filament Data Download | 3D Printer Filament Test Data',
    seoDescription: 'Download bending test data (CSV format) for 3D printer filaments. Use stress-strain curve data for personal use, research, and learning.',
    seoKeywords: 'filament data,download,CSV,bending test,stress strain,3D printer,PLA,PETG,ABS',
    description: 'Download bending test data (CSV format) for 3D printer filaments.',
    description2: 'Use stress-strain curve data for personal use, research, and learning.',
    dataUsageTitle: 'Data Usage (Important)',
    dataUsagePersonal: 'Personal use is free.',
    dataUsagePersonalDetail: 'Feel free to use for research, learning, and personal projects.',
    dataUsagePublic: 'For publication in videos, articles, SNS, or redistribution of data, please contact us in advance.',
    dataUsageDetail: 'For details, see ',
    dataUsageLink: 'Data Usage Policy',
    dataUsageConfirm: '.',
    csvListTitle: 'Bending Test Data (CSV)',
    csvListDescription: 'Download stress-strain curve data in CSV format. Each file contains test conditions (span, specimen dimensions) and data points (strain %, stress MPa).',
    loading: 'Loading data...',
    error: 'Failed to load data',
    noData: 'No data available for download.',
    filterMaterial: 'Material (multiple)',
    filterManufacturer: 'Manufacturer (multiple)',
    filterPattern: 'Pattern (multiple)',
    filterKeyword: 'Keyword',
    filterKeywordPlaceholder: 'Search...',
    filterClear: 'Clear',
    filterDisplay: 'Showing:',
    filterTotal: 'items',
    filterAllTotal: '(of ',
    filterAllTotalEnd: ' total)',
    tableHeaderMaterial: 'Material',
    tableHeaderManufacturer: 'Manufacturer',
    tableHeaderColor: 'Color',
    tableHeaderPurchase: 'Purchase',
    tableHeaderCSV: 'CSV (Pattern)',
    purchaseAmazon: 'Amazon',
    purchaseOfficial: 'Official',
    pattern1: 'Pattern 1',
    pattern2: 'Pattern 2',
    pattern3: 'Pattern 3',
    dataFormatTitle: 'Data Format',
    dataFormatCSV: 'CSV Format:',
    dataFormatHeader: 'Header row (starts with #): Test conditions (span, specimen dimensions)',
    dataFormatColumns: 'Data columns: ',
    dataFormatStrain: ' (strain %), ',
    dataFormatStress: ' (stress MPa)',
    dataFormatEncoding: 'Encoding: UTF-8',
    dataFormatDelimiter: 'Delimiter: Comma (,)',
    patternAbout: 'About patterns:',
    patternDesc1: ': Horizontal (most common)',
    patternDesc2: ': Z-axis',
    patternDesc3: ': X-axis',
    testMethodLink: 'Bending strength test method (measurement conditions)',
    noticeTitle: 'Notice',
    notice1: 'Data is calculated from 3-point bending test results.',
    notice2: 'Results vary depending on test conditions (span, specimen dimensions, speed, etc.).',
    notice3: 'Data is for reference only and does not guarantee performance in actual use.',
    notice4: 'We strive for accuracy, but errors may exist.',
    notice5: 'Data may be updated or added without notice.',
    backToList: 'Back to filament list',
    breadcrumbHome: 'Home',
    breadcrumbFilament: 'Filament Data',
    breadcrumbDownload: 'Data Download',
    mobileMaterial: 'Material:',
    mobileManufacturer: 'Manufacturer:',
    mobileColor: 'Color:',
    mobilePurchase: 'Purchase:',
    mobileCSV: 'CSV (Pattern):'
  };

  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;
  const prefix = isEnglish ? '/en' : '';

  useSEO({
    title: TEXT.seoTitle,
    description: TEXT.seoDescription,
    keywords: TEXT.seoKeywords,
    canonicalPath: '/filament/data-download'
  });

  const [csvList, setCsvList] = useState<ExtendedCSVIndexEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('type');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // フィルター状態（複数選択対応）
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [selectedManufacturers, setSelectedManufacturers] = useState<string[]>([]);
  const [selectedPatterns, setSelectedPatterns] = useState<string[]>([]);
  const [filterKeyword, setFilterKeyword] = useState<string>('');

  useEffect(() => {
    const fetchCSVIndex = async () => {
      try {
        const response = await fetch('/bending/csv/index.json');
        if (!response.ok) {
          throw new Error('CSVインデックスの取得に失敗しました');
        }
        const data = await response.json();
        setCsvList(data);
      } catch (err) {
        console.error('CSVインデックス取得エラー:', err);
        setError('データの読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchCSVIndex();
  }, []);

  // グルーピング用のキー生成（厳密一致）
  const getGroupKey = (maker: string, type: string, color: string): string => {
    return `${maker.trim().toUpperCase()}_${type.trim().toUpperCase()}_${color.trim().toUpperCase()}`;
  };

  // フィラメント単位でグルーピング
  const groupByFilament = (entries: ExtendedCSVIndexEntry[]): GroupedFilamentRow[] => {
    const groups = new Map<string, GroupedFilamentRow>();
    
    for (const entry of entries) {
      const key = getGroupKey(entry.maker, entry.type, entry.color);
      
      if (!groups.has(key)) {
        groups.set(key, {
          maker: entry.maker,
          type: entry.type,
          color: entry.color,
          buyLinks: entry.buyLinks,
          patternLinks: {}
        });
      }
      
      const group = groups.get(key)!;
      const patternNum = orientationToPattern(entry.orientation);
      if (patternNum > 0) {
        // パスが短い（サフィックスがないオリジナル）を優先するため、
        // 未設定、もしくは既存のパスより短い場合のみ上書きする
        if (!group.patternLinks[patternNum] || entry.path.length < group.patternLinks[patternNum].length) {
          group.patternLinks[patternNum] = entry.path;
        }
      }
    }
    
    return Array.from(groups.values());
  };

  // フィルター選択肢を自動生成（グルーピング前のデータから）
  const uniqueMaterials = Array.from(new Set(csvList.map(e => e.type))).sort();
  const uniqueMakers = Array.from(new Set(csvList.map(e => e.maker))).sort();
  const uniquePatterns = ['パターン1', 'パターン2', 'パターン3'];

  // フィルター適用（グルーピング後の行に対して）
  const getFilteredList = () => {
    const grouped = groupByFilament(csvList);
    
    return grouped.filter(row => {
      // 材料フィルター（OR条件）
      if (selectedMaterials.length > 0 && !selectedMaterials.includes(row.type)) {
        return false;
      }
      
      // メーカーフィルター（OR条件）
      if (selectedManufacturers.length > 0 && !selectedManufacturers.includes(row.maker)) {
        return false;
      }
      
      // パターンフィルター（OR条件：選択されたパターンのいずれかが存在する）
      if (selectedPatterns.length > 0) {
        const hasAnySelectedPattern = selectedPatterns.some(pattern => {
          const patternNum = parseInt(pattern.replace('パターン', ''));
          return row.patternLinks[patternNum];
        });
        if (!hasAnySelectedPattern) return false;
      }
      
      // キーワード検索
      if (filterKeyword) {
        const keyword = filterKeyword.toLowerCase();
        const searchText = `${row.type} ${row.maker} ${row.color}`.toLowerCase();
        if (!searchText.includes(keyword)) return false;
      }
      
      return true;
    });
  };

  // フィルター解除
  const handleClearFilters = () => {
    setSelectedMaterials([]);
    setSelectedManufacturers([]);
    setSelectedPatterns([]);
    setFilterKeyword('');
  };

  // ソート処理
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  // フィルター後にソート
  const getSortedList = () => {
    const filtered = getFilteredList();
    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[sortKey] || '';
      let bVal = b[sortKey] || '';
      
      // 大文字小文字を統一
      aVal = aVal.toString().toUpperCase();
      bVal = bVal.toString().toUpperCase();
      
      // 空欄は最後に
      if (!aVal && bVal) return 1;
      if (aVal && !bVal) return -1;
      if (!aVal && !bVal) return 0;
      
      const comparison = aVal.localeCompare(bVal);
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return sorted;
  };

  // ソートインジケーター
  const SortIndicator: React.FC<{ columnKey: SortKey }> = ({ columnKey }) => {
    if (sortKey !== columnKey) return null;
    return (
      <span className="ml-1 text-accent">
        {sortOrder === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  // 材料セルのレンダリング（リンク化）
  const renderMaterialCell = (materialName: string) => {
    const path = getFilamentPagePath(materialName);
    const displayLabel = formatMaterialLabel(materialName);
    
    if (path) {
      return (
        <Link 
          to={path} 
          className="text-accent hover:text-accent-orange transition-colors"
        >
          {displayLabel}
        </Link>
      );
    }
    
    // パスが取れない場合はテキストのまま
    return displayLabel;
  };

  // 購入リンクのレンダリング（index.json由来、縦並び）
  const renderPurchaseLinks = (maker: string, buyLinks?: { amazon?: string; official?: string }) => {
    if (!buyLinks || (!buyLinks.amazon && !buyLinks.official)) {
      return <span className="text-metal-light text-xs">-</span>;
    }
    
    return (
      <div className="flex flex-col items-center justify-center gap-1">
        {buyLinks.amazon && (
          <a
            href={buyLinks.amazon}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:text-accent-orange text-xs transition-colors whitespace-nowrap"
            onClick={(e) => e.stopPropagation()}
          >
            {TEXT.purchaseAmazon}
          </a>
        )}
        {buyLinks.official && (
          <a
            href={buyLinks.official}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:text-accent-orange text-xs transition-colors whitespace-nowrap"
            onClick={(e) => e.stopPropagation()}
          >
            {isEnglish ? `${maker} ${TEXT.purchaseOfficial}` : `${maker}${TEXT.purchaseOfficial}`}
          </a>
        )}
      </div>
    );
  };

  // パターン別CSVリンクのレンダリング（3スロット固定）
  const renderPatternLinks = (patternLinks: Record<number, string>) => {
    const patterns = [1, 2, 3];
    
    return (
      <div className="grid grid-cols-3 gap-2 items-center justify-items-center">
        {patterns.map(patternNum => {
          const csvUrl = patternLinks[patternNum];
          const patternLabel = patternNum === 1 ? TEXT.pattern1 : patternNum === 2 ? TEXT.pattern2 : TEXT.pattern3;
          
          if (csvUrl) {
            // CSVが存在する場合：ダウンロードリンクを表示
            return (
              <a
                key={patternNum}
                href={csvUrl}
                download
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-accent hover:bg-accent-orange text-graphite font-semibold text-xs rounded transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line"></i>
                {patternLabel}
              </a>
            );
          } else {
            // CSVが存在しない場合：空枠を表示（幅を確保）
            return (
              <div
                key={patternNum}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-transparent text-xs whitespace-nowrap pointer-events-none"
              >
                <i className="ri-download-2-line"></i>
                {patternLabel}
              </div>
            );
          }
        })}
      </div>
    );
  };

  const breadcrumbItems = [
    { label: TEXT.breadcrumbHome, path: prefix + '/' },
    { label: TEXT.breadcrumbFilament, path: prefix + '/filament' },
    { label: TEXT.breadcrumbDownload }
  ];

  const sortedList = getSortedList();
  const filteredList = getFilteredList();
  const totalEntries = groupByFilament(csvList).length;

  // チェックボックスのトグル処理
  const toggleSelection = (value: string, selectedList: string[], setSelectedList: React.Dispatch<React.SetStateAction<string[]>>) => {
    if (selectedList.includes(value)) {
      setSelectedList(selectedList.filter(v => v !== value));
    } else {
      setSelectedList([...selectedList, value]);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid" style={{ scrollbarGutter: 'stable' }} data-lang={isEnglish ? 'en' : 'ja'}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            {/* タイトル */}
            <div className="text-center mt-8">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                {TEXT.title}
              </h1>
              <div className="section-divider max-w-md mx-auto mb-6"></div>
              <p className="text-metal-light max-w-3xl mx-auto leading-relaxed text-sm sm:text-base">
                {TEXT.description}<br />
                {TEXT.description2}
              </p>
            </div>

            {/* データ利用に関する注意書き（強調） */}
            <div className="metal-panel p-4 sm:p-6 border-2 border-accent/50">
              <div className="flex items-start space-x-3">
                <i className="ri-alert-line text-accent text-2xl mt-0.5 flex-shrink-0"></i>
                <div className="space-y-3">
                  <h2 className="text-white font-bold font-['Exo'] text-lg sm:text-xl">
                    {TEXT.dataUsageTitle}
                  </h2>
                  <div className="text-sm sm:text-base text-metal-light space-y-2">
                    <p>
                      <strong className="text-white">{TEXT.dataUsagePersonal}</strong>{TEXT.dataUsagePersonalDetail}
                    </p>
                    <p>
                      <strong className="text-accent">{TEXT.dataUsagePublic}</strong>
                    </p>
                    <p>
                      {TEXT.dataUsageDetail}<Link to={prefix + "/data-policy"} className="text-accent hover:text-accent-orange underline font-semibold transition-colors">{TEXT.dataUsageLink}</Link>{TEXT.dataUsageConfirm}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CSV一覧 */}
            <div className="metal-panel p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2 flex items-center gap-2">
                <i className="ri-file-list-3-line text-accent"></i>
                {TEXT.csvListTitle}
              </h2>
              <p className="text-metal-light text-xs sm:text-sm mb-6">
                {TEXT.csvListDescription}
              </p>

              {loading && (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center gap-3">
                    <i className="ri-loader-4-line text-accent text-2xl animate-spin"></i>
                    <span className="text-metal-light text-sm">{TEXT.loading}</span>
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-start space-x-3 bg-metal p-4 sm:p-6 rounded">
                  <i className="ri-error-warning-line text-accent-orange text-lg mt-0.5"></i>
                  <p className="text-metal-light text-xs sm:text-sm">{error}</p>
                </div>
              )}

              {!loading && !error && csvList.length === 0 && (
                <div className="flex items-start space-x-3 bg-metal p-4 sm:p-6 rounded">
                  <i className="ri-information-line text-metal-light text-lg mt-0.5"></i>
                  <p className="text-metal-light text-xs sm:text-sm">
                    {TEXT.noData}
                  </p>
                </div>
              )}

              {!loading && !error && csvList.length > 0 && (
                <>
                  {/* フィルターバー */}
                  <div className="metal-panel p-4 mb-6 bg-metal/30 w-full">
                    <div className="flex flex-col gap-4">
                      {/* フィルター項目 */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* 材料（チェックボックス） */}
                        <div>
                          <label className="block text-xs text-metal-light mb-2">{TEXT.filterMaterial}</label>
                          <div className="bg-graphite border border-metal-light/30 rounded px-3 py-2 max-h-48 overflow-y-auto">
                            <div className="space-y-1">
                              {uniqueMaterials.map(material => (
                                <label key={material} className="flex items-center gap-2 text-sm text-white cursor-pointer hover:text-accent transition-colors">
                                  <input
                                    type="checkbox"
                                    checked={selectedMaterials.includes(material)}
                                    onChange={() => toggleSelection(material, selectedMaterials, setSelectedMaterials)}
                                    className="cursor-pointer"
                                  />
                                  <span>{material}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* メーカー（チェックボックス） */}
                        <div>
                          <label className="block text-xs text-metal-light mb-2">{TEXT.filterManufacturer}</label>
                          <div className="bg-graphite border border-metal-light/30 rounded px-3 py-2 max-h-48 overflow-y-auto">
                            <div className="space-y-1">
                              {uniqueMakers.map(maker => (
                                <label key={maker} className="flex items-center gap-2 text-sm text-white cursor-pointer hover:text-accent transition-colors">
                                  <input
                                    type="checkbox"
                                    checked={selectedManufacturers.includes(maker)}
                                    onChange={() => toggleSelection(maker, selectedManufacturers, setSelectedManufacturers)}
                                    className="cursor-pointer"
                                  />
                                  <span>{maker}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* パターン（チェックボックス） */}
                        <div>
                          <label className="block text-xs text-metal-light mb-2">{TEXT.filterPattern}</label>
                          <div className="bg-graphite border border-metal-light/30 rounded px-3 py-2">
                            <div className="space-y-1">
                              {uniquePatterns.map(pattern => (
                                <label key={pattern} className="flex items-center gap-2 text-sm text-white cursor-pointer hover:text-accent transition-colors">
                                  <input
                                    type="checkbox"
                                    checked={selectedPatterns.includes(pattern)}
                                    onChange={() => toggleSelection(pattern, selectedPatterns, setSelectedPatterns)}
                                    className="cursor-pointer"
                                  />
                                  <span>{pattern}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* キーワード検索 */}
                        <div>
                          <label className="block text-xs text-metal-light mb-2">{TEXT.filterKeyword}</label>
                          <input
                            type="text"
                            value={filterKeyword}
                            onChange={(e) => setFilterKeyword(e.target.value)}
                            placeholder={TEXT.filterKeywordPlaceholder}
                            className="w-full bg-graphite border border-metal-light/30 text-white text-sm rounded px-3 py-2 focus:outline-none focus:border-accent transition-colors placeholder-metal-light/50"
                          />
                        </div>
                      </div>

                      {/* クリアボタン＋件数表示 */}
                      <div className="flex items-center gap-3">
                        <button
                          onClick={handleClearFilters}
                          className="flex items-center gap-2 px-4 py-2 bg-metal hover:bg-metal-light/20 text-metal-light hover:text-white text-sm rounded transition-colors whitespace-nowrap"
                        >
                          <i className="ri-close-circle-line"></i>
                          {TEXT.filterClear}
                        </button>
                        <div className="text-sm text-metal-light whitespace-nowrap">
                          {TEXT.filterDisplay} <span className="text-white font-semibold">{filteredList.length}</span> {TEXT.filterTotal}
                          {filteredList.length !== totalEntries && (
                            <span className="text-xs ml-2">{TEXT.filterAllTotal}{totalEntries}{TEXT.filterAllTotalEnd}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* デスクトップ表示 */}
                  <div className="hidden sm:block overflow-x-auto w-full">
                    <table className="w-full" style={{ tableLayout: 'fixed' }}>
                      <thead>
                        <tr className="bg-[#3a3d42]">
                          <th 
                            className="px-4 py-3 text-left text-sm font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer hover:bg-metal/30 transition-colors"
                            onClick={() => handleSort('type')}
                            style={{ width: '15%' }}
                          >
                            {TEXT.tableHeaderMaterial}
                            <SortIndicator columnKey="type" />
                          </th>
                          <th 
                            className="px-4 py-3 text-left text-sm font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer hover:bg-metal/30 transition-colors"
                            onClick={() => handleSort('maker')}
                            style={{ width: '15%' }}
                          >
                            {TEXT.tableHeaderManufacturer}
                            <SortIndicator columnKey="maker" />
                          </th>
                          <th 
                            className="px-4 py-3 text-left text-sm font-semibold text-white font-['Exo'] whitespace-nowrap cursor-pointer hover:bg-metal/30 transition-colors"
                            onClick={() => handleSort('color')}
                            style={{ width: '15%' }}
                          >
                            {TEXT.tableHeaderColor}
                            <SortIndicator columnKey="color" />
                          </th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-white font-['Exo'] whitespace-nowrap" style={{ width: '15%' }}>
                            {TEXT.tableHeaderPurchase}
                          </th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-white font-['Exo'] whitespace-nowrap" style={{ width: '40%' }}>
                            {TEXT.tableHeaderCSV}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-metal">
                        {sortedList.map((row, index) => (
                          <tr key={index} className="hover:bg-metal/30 transition-colors">
                            <td className="px-4 py-3 text-sm text-white font-medium">
                              {renderMaterialCell(row.type)}
                            </td>
                            <td className="px-4 py-3 text-sm text-white">
                              {row.maker}
                            </td>
                            <td className="px-4 py-3 text-sm text-metal-light">
                              {row.color}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {renderPurchaseLinks(row.maker, row.buyLinks)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {renderPatternLinks(row.patternLinks)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* モバイル表示 */}
                  <div className="sm:hidden space-y-3">
                    {sortedList.map((row, index) => (
                      <div key={index} className="bg-metal/30 p-4 rounded border border-metal-light/20">
                        <div className="space-y-2 mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-metal-light">{TEXT.mobileMaterial}</span>
                            <span className="text-sm text-white font-medium">
                              {renderMaterialCell(row.type)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-metal-light">{TEXT.mobileManufacturer}</span>
                            <span className="text-sm text-white">{row.maker}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-metal-light">{TEXT.mobileColor}</span>
                            <span className="text-sm text-metal-light">{row.color}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-metal-light">{TEXT.mobilePurchase}</span>
                            {renderPurchaseLinks(row.maker, row.buyLinks)}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="text-xs text-metal-light mb-1">{TEXT.mobileCSV}</div>
                          {renderPatternLinks(row.patternLinks)}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* データ形式の説明 */}
            <div className="metal-panel p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2 flex items-center gap-2">
                <i className="ri-information-line text-accent"></i>
                {TEXT.dataFormatTitle}
              </h2>
              <div className="space-y-4 text-xs sm:text-sm text-metal-light">
                <div>
                  <strong className="text-white">{TEXT.dataFormatCSV}</strong>
                  <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                    <li>{TEXT.dataFormatHeader}</li>
                    <li>{TEXT.dataFormatColumns}<code className="text-accent">strain_pct</code>{TEXT.dataFormatStrain}<code className="text-accent">stress_mpa</code>{TEXT.dataFormatStress}</li>
                    <li>{TEXT.dataFormatEncoding}</li>
                    <li>{TEXT.dataFormatDelimiter}</li>
                  </ul>
                </div>
                <div>
                  <strong className="text-white">{TEXT.patternAbout}</strong>
                  <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                    <li><strong>{TEXT.pattern1}</strong>{TEXT.patternDesc1}</li>
                    <li><strong>{TEXT.pattern3}</strong>{TEXT.patternDesc3}</li>
                    <li><strong>{TEXT.pattern2}</strong>{TEXT.patternDesc2}</li>
                  </ul>
                  <p className="mt-3 text-xs">
                    <Link 
                      to={prefix + "/filament/bend-strength-test"}
                      className="text-accent hover:text-accent-orange transition-colors inline-flex items-center gap-1"
                    >
                      <i className="ri-flask-line"></i>
                      {TEXT.testMethodLink}
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            {/* 注意事項 */}
            <div className="metal-panel p-4 sm:p-6">
              <div className="flex items-start space-x-3">
                <i className="ri-information-line text-accent text-xl mt-1"></i>
                <div className="space-y-2">
                  <h4 className="text-white font-semibold font-['Exo'] text-sm sm:text-base">
                    {TEXT.noticeTitle}
                  </h4>
                  <ul className="text-metal-light text-xs sm:text-sm space-y-1 list-disc list-inside">
                    <li>{TEXT.notice1}</li>
                    <li>{TEXT.notice2}</li>
                    <li>{TEXT.notice3}</li>
                    <li>{TEXT.notice4}</li>
                    <li>{TEXT.notice5}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 戻るリンク */}
            <div className="text-center">
              <Link 
                to={prefix + "/filament"}
                className="inline-flex items-center gap-2 text-accent hover:text-accent-orange font-medium transition-colors text-sm sm:text-base"
              >
                <i className="ri-arrow-left-line"></i>
                {TEXT.backToList}
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DataDownloadPage;
